import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { Button, Form, Input } from 'antd';
import { useForm } from 'antd/es/form/Form';
import ReactInputMask, { Props as InputMaskProps } from 'react-input-mask';
import { renderRequiredMark, rules } from 'common/helpers/form.helper';
import { PHONE_MASK, PHONE_MASK_BY, PHONE_PATTERN_BY, PHONE_PATTERN_WITH_CODE } from 'common/config';
import { normalizePhone } from 'common/helpers';
import { useCaptcha } from 'common/hooks/useCaptcha';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { selectAuthError, sendEmail, sendPhone, setAuthError, verifyEmail, verifyPhone } from 'entities/Auth/Auth.slice';

const InputMask = ReactInputMask as React.ComponentType<InputMaskProps>;

enum ELoginStep {
  Send = 'send',
  Verify = 'verify',
}

enum ELoginType {
  Email = 'email',
  Phone = 'phone',
}

interface IComponentProps {
  isRegistration?: boolean;
}

export const LoginByOtp: FC<IComponentProps> = ({ isRegistration }) => {
  const dispatch = useAppDispatch();
  // auth
  const authError = useAppSelector(selectAuthError);

  const [step, setStep] = useState<ELoginStep>(ELoginStep.Send);
  const [type, setType] = useState<ELoginType>(ELoginType.Email);
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [phoneMask, setPhoneMask] = useState<string | null>(null);
  const [form] = useForm();
  const emailRef = useRef<any>(null);
  const phoneRef = useRef<any>(null);
  const { trigger, loading, captchaNode } = useCaptcha();

  const isVerifyStep = step === ELoginStep.Verify;
  const isEmailType = type === ELoginType.Email;
  const isPhoneType = type === ELoginType.Phone;

  useEffect(() => {
    if (isRegistration) {
      setType(ELoginType.Phone);
    }
  }, [isRegistration]);

  useEffect(() => {
    if (authError) {
      form.setFields([
        {
          name: isVerifyStep ? 'code' : isEmailType ? 'email' : 'phone',
          errors: [authError],
        },
      ]);
    }
  }, [authError, step, type]);

  const changeEmail = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    const digits = normalizePhone(value);

    const isPhone = digits.length >= 4 && (value.startsWith('+') || value.length === digits.length);

    if (isPhone) {
      const isBelarus = digits.startsWith('375');
      const mask = isBelarus ? PHONE_MASK_BY : PHONE_MASK;
      const prefixLength = isBelarus ? 3 : 1;
      const remaining = digits.slice(prefixLength);
      setPhoneMask(mask);
      setType(ELoginType.Phone);
      form.setFieldValue('phone', remaining);
      requestAnimationFrame(() => {
        const input = phoneRef.current?.input;

        if (!input) {
          return;
        }

        input.focus();
      });
    }
  };

  const changePhone = (e: ChangeEvent<HTMLInputElement>): void => {
    if (phoneMask) {
      return;
    }

    const digits = normalizePhone(e.target.value);

    if (digits.startsWith('375')) {
      setPhoneMask(PHONE_MASK_BY);
      form.setFieldValue('phone', digits.slice(3));
      requestAnimationFrame(() => phoneRef.current?.input?.focus());
    } else if (digits.startsWith('7') || digits.startsWith('8')) {
      setPhoneMask(PHONE_MASK);
      form.setFieldValue('phone', digits.slice(1));
      requestAnimationFrame(() => phoneRef.current?.input?.focus());
    }
  };

  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!phoneMask || (e.key !== 'Backspace' && e.key !== 'Delete')) {
      return;
    }

    const digits = normalizePhone(form.getFieldValue('phone') || '');
    // +375 (BY) prefix occupies 3 digits; +7 (RU/KZ) occupies 1 — clear mask when nothing left to delete
    const prefixDigits = phoneMask === PHONE_MASK_BY ? 3 : 1;

    if (digits.length <= prefixDigits) {
      e.preventDefault();
      setPhoneMask(null);
      form.setFieldValue('phone', '');

      if (!isRegistration) {
        setType(ELoginType.Email);
        form.setFieldValue('email', '');
        requestAnimationFrame(() => {
          const input = emailRef.current?.input;

          if (!input) {
            return;
          }

          input.focus();
        });
      } else {
        requestAnimationFrame(() => {
          phoneRef.current?.input?.focus();
        });
      }
    }
  };

  const handleSendEmail = async (email: string): Promise<void> => {
    await dispatch(
      sendEmail({
        email,
        onSuccess: () => {
          setEmail(email);
          setStep(ELoginStep.Verify);
          form.resetFields(['code']);
        },
      }),
    );
  };

  const handleSendPhone = async (phone: string): Promise<void> => {
    const normalizedPhone = normalizePhone(phone);

    await dispatch(
      sendPhone({
        phone: normalizedPhone,
        isRegistration,
        onSuccess: () => {
          setPhone(normalizedPhone);
          setStep(ELoginStep.Verify);
          form.resetFields(['code']);
        },
      }),
    );
  };

  const handleVerifyEmail = async (code: string): Promise<void> => {
    await dispatch(verifyEmail({ email, code }));
  };

  const handleVerifyPhone = async (code: string): Promise<void> => {
    await dispatch(verifyPhone({ phone, code }));
  };

  const handleSubmit = (values: { email?: string; phone?: string; code?: string }): void => {
    dispatch(setAuthError(null));

    if (isVerifyStep) {
      trigger(() => (isEmailType ? handleVerifyEmail(values.code as string) : handleVerifyPhone(values.code as string)));
    } else {
      trigger(() => (isEmailType ? handleSendEmail(values.email as string) : handleSendPhone(values.phone as string)));
    }
  };

  if (isVerifyStep) {
    return (
      <Form
        className="auth-card__content-form"
        form={form}
        layout="vertical"
        requiredMark={renderRequiredMark}
        onFinish={handleSubmit}
      >
        <div className="mb-11 auth-card__content-form-title">
          {isRegistration ? `Введите код, отправленный на ${phone}` : 'Введите полученный код из письма/СМС'}
        </div>

        <Form.Item className="w-100" label={isRegistration ? 'Код' : 'Код из письма/СМС'} name="code" rules={[rules.required()]}>
          <Input placeholder="Введите код" />
        </Form.Item>

        {captchaNode}

        <Button className="button-lg primary w-100 mb-5" loading={loading} htmlType="submit">
          Войти
        </Button>
      </Form>
    );
  }

  return (
    <Form
      className="auth-card__content-form"
      form={form}
      layout="vertical"
      requiredMark={renderRequiredMark}
      onFinish={handleSubmit}
    >
      <div className="mb-11 auth-card__content-form-title">
        {isRegistration ? 'Введите номер телефона для получения кода' : 'Введите почту или телефон для получения кода'}
      </div>

      <Form.Item
        className="w-full"
        hidden={!isEmailType}
        label="Адрес почты/номер телефона"
        name="email"
        rules={isEmailType ? [rules.required(), rules.type('email', 'Неверный формат электронной почты или телефона')] : []}
      >
        <Input ref={emailRef} placeholder="Введите почту или телефон" onChange={changeEmail} />
      </Form.Item>

      <Form.Item
        className="w-full"
        hidden={!isPhoneType}
        label={isRegistration ? 'Телефон' : 'Адрес почты/номер телефона'}
        name="phone"
        rules={
          isPhoneType
            ? [
                rules.required(),
                ...(phoneMask
                  ? [
                      rules.pattern(
                        phoneMask === PHONE_MASK_BY ? PHONE_PATTERN_BY : PHONE_PATTERN_WITH_CODE,
                        'Неверный формат телефона',
                      ),
                    ]
                  : []),
              ]
            : []
        }
      >
        {phoneMask ? (
          <InputMask mask={phoneMask} onChange={changePhone}>
            {(inputProps: object) => (
              <Input
                {...inputProps}
                ref={phoneRef}
                onKeyDown={handlePhoneKeyDown}
                placeholder={phoneMask === PHONE_MASK_BY ? '+375 (__) ___-__-__' : '+7 (___) ___-__-__'}
              />
            )}
          </InputMask>
        ) : (
          <Input ref={phoneRef} placeholder="Введите номер телефона" onChange={changePhone} onKeyDown={handlePhoneKeyDown} />
        )}
      </Form.Item>

      {captchaNode}

      <Button className="button-lg primary w-100" loading={loading} htmlType="submit">
        Получить код
      </Button>
    </Form>
  );
};
